import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { DateCell } from '../../components/DateCell/DateCell';
import { ReportsStatusCell } from '../../components/ReportsStatusCell/ReportsStatusCell';
import { StyledLink } from '../../components/StyledLink/StyledLink';
import { TextCell } from '../../components/TextCell/TextCell';
import { filterArrayByNullAndUndefined } from '../../mappers/common/common';
import {
    addSettingsToDecl,
    arrangeInOrder
} from '../../service/DeclarationHelper';
import {
    ColumnsSettings,
    ColumnsSettingsViewDict,
    reportsDomain
} from '../../typings/common';
import { GridRow } from '../../typings/domain';
import { OfflineReportState } from '../../typings/graphql-grid';
import {
    GridCellProps,
    GridColumnDecl,
    GridColumnStaticDecl
} from '../../typings/grid';
import { ReportDomain } from '../../typings/reportsDomain';

import * as keyset from './reportsDomain.i18n';

const i18n = i18nFactory(keyset);

export enum ReportDomainColumnKey {
    id = 'id',
    buildDate = 'buildDate',
    startDate = 'startDate',
    endDate = 'endDate',
    status = 'status',
    link = 'link'
}

export const reportsDomainColumnsOrder = [
    'id',
    'buildDate',
    'startDate',
    'endDate',
    'status',
    'link'
];

export function getDynamicReportsDomainColumnsFields(): ColumnsSettings {
    return {
        columnOrder: reportsDomainColumnsOrder,
        columnViewInfo: {
            id: {
                width: 90,
                widthCompact: 90,
                hidden: false
            },
            buildDate: {
                width: 200,
                widthCompact: 200,
                hidden: false
            },
            startDate: {
                width: 150,
                widthCompact: 150,
                hidden: false
            },
            endDate: {
                width: 150,
                widthCompact: 150,
                hidden: false
            },
            status: {
                width: 120,
                widthCompact: 120,
                hidden: false
            },
            link: {
                width: 150,
                widthCompact: 150,
                hidden: false
            }
        }
    };
}

function getFixedReportsColumns(): Array<GridColumnStaticDecl<ReportDomain>> {
    return [
        {
            title: i18n('id'),
            key: ReportDomainColumnKey.id,
            isSortable: false,
            fixed: true,
            CellComponent: React.memo(
                ({
                    rowData: { id }
                }: GridCellProps<ReportDomain>): JSX.Element => (
                    <TextCell>{id}</TextCell>
                )
            )
        }
    ];
}

function getReportsDomainColumnDecl(): Array<
    GridColumnStaticDecl<ReportDomain>
> {
    return filterArrayByNullAndUndefined<GridColumnStaticDecl<ReportDomain>>([
        {
            title: i18n('build-date'),
            key: ReportDomainColumnKey.buildDate,
            isSortable: false,
            fixed: false,
            CellComponent: React.memo(
                ({
                    rowData: { buildDate }
                }: GridCellProps<ReportDomain>): JSX.Element => (
                    <DateCell date={buildDate} />
                )
            )
        },
        {
            title: i18n('start-date'),
            key: ReportDomainColumnKey.startDate,
            isSortable: false,
            fixed: false,
            CellComponent: React.memo(
                ({
                    rowData: { dateStart }
                }: GridCellProps<ReportDomain>): JSX.Element => (
                    <DateCell date={dateStart} showTime={false} />
                )
            )
        },
        {
            title: i18n('end-date'),
            key: ReportDomainColumnKey.endDate,
            isSortable: false,
            fixed: false,
            CellComponent: React.memo(
                ({
                    rowData: { dateEnd }
                }: GridCellProps<ReportDomain>): JSX.Element => (
                    <DateCell date={dateEnd} showTime={false} />
                )
            )
        },
        {
            title: i18n('status'),
            key: ReportDomainColumnKey.status,
            isSortable: false,
            fixed: false,
            CellComponent: React.memo(
                ({
                    rowData: { status }
                }: GridCellProps<ReportDomain>): JSX.Element => (
                    <ReportsStatusCell status={status} />
                )
            )
        },
        {
            title: i18n('link'),
            key: ReportDomainColumnKey.link,
            isSortable: false,
            fixed: false,
            CellComponent: React.memo(
                ({
                    rowData: { status, reportUrl }
                }: GridCellProps<ReportDomain>): JSX.Element => {
                    return (
                        <TextCell>
                            <StyledLink
                                href={reportUrl || ''}
                                disabled={status !== OfflineReportState.READY}
                                title={i18n('download')}
                            >
                                {i18n('download')}
                            </StyledLink>
                        </TextCell>
                    );
                }
            )
        }
    ]);
}

export function getReportsDomainColumns(
    columnsSettings: ColumnsSettingsViewDict
): Array<GridColumnDecl<GridRow>> {
    const fixedColumns = getFixedReportsColumns().map(column => {
        return addSettingsToDecl(column, columnsSettings, reportsDomain);
    });

    const result = getReportsDomainColumnDecl().map(column => {
        return addSettingsToDecl(column, columnsSettings, reportsDomain);
    });
    return fixedColumns.concat(
        arrangeInOrder(result, columnsSettings[reportsDomain].columnOrder)
    );
}
